/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import { authService, type LoginResponse } from '../services/auth.service';
import type { User, AuthContextType } from '../types';
import { isTokenExpired } from '../utils/token';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function getInitialUser(): User | null {
  if (typeof window === 'undefined') return null;
  const token = localStorage.getItem('customer_token');
  const savedUser = localStorage.getItem('customer_user');

  if (token && savedUser) {
    if (isTokenExpired(token)) {
      console.warn('⚠️ [AuthContext] Token expirado no localStorage, limpando dados da sessão');
      localStorage.removeItem('customer_token');
      localStorage.removeItem('customer_user');
      localStorage.removeItem('customer_empresas');
      localStorage.removeItem('customer_empresa_id');
      return null;
    }
    try {
      return JSON.parse(savedUser);
    } catch (error) {
      console.error('Erro ao carregar dados do usuário:', error);
      localStorage.removeItem('customer_token');
      localStorage.removeItem('customer_user');
      return null;
    }
  }
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(getInitialUser);
  const loading = false;

  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login({ email, password });
      
      // Check if password change is required
      if (
        ('requires_password_change' in response && response.requires_password_change) ||
        ('must_change_password' in response && Boolean((response as { must_change_password?: boolean }).must_change_password))
      ) {
        return { 
          success: false, 
          requiresPasswordChange: true, 
          message: ('message' in response && response.message) || 'Você precisa alterar sua senha temporária antes de continuar.' 
        };
      }
      
      // Check if 2FA is required
      if ('requires_2fa' in response && response.requires_2fa) {
        return { 
          success: false, 
          requires2FA: true, 
          message: response.message || 'Código de verificação enviado para o seu email.' 
        };
      }

      // Normal login flow - type guard para LoginResponse
      if ('access_token' in response && 'user' in response && response.access_token && response.user) {
        const loginResponse = response as LoginResponse;
        if (!loginResponse.empresas || loginResponse.empresas.length === 0) {
          return {
            success: false,
            error: 'Acesso negado. Nenhum solicitante ativo vinculado a este usuário.',
          };
        }
        const userData: User = {
          id: loginResponse.user.id,
          name: loginResponse.user.name,
          email: loginResponse.user.email,
          role: loginResponse.user.role,
          group_id: loginResponse.user.group_id,
        };

        // Limpar dados de agendamento do localStorage antes de salvar novo login
        // Seguindo o mesmo padrão do scheduling
        console.log('🧹 [AuthContext] Limpando dados de agendamento do localStorage');
        const keysToRemove = [
          'customer_employeesData',
          'customer_examsData',
          'customer_stepData',
          'employeesData',
          'examsData',
          'stepData',
          'orderData',
          'pedidoData',
          'pedidoId',
          'pixData',
          'pixError',
          'employeeFormData',
        ];
        
        try {
          keysToRemove.forEach((key) => {
            localStorage.removeItem(key);
          });
          console.log('✅ [AuthContext] Dados de agendamento removidos do localStorage');
        } catch (error) {
          console.error('❌ [AuthContext] Erro ao limpar localStorage:', error);
        }

        localStorage.setItem('customer_token', loginResponse.access_token);
        localStorage.setItem('customer_user', JSON.stringify(userData));
        
        // Save empresas if available
        if (loginResponse.empresas && loginResponse.empresas.length > 0) {
          localStorage.setItem('customer_empresas', JSON.stringify(loginResponse.empresas));
          // Select first empresa by default
          localStorage.setItem('customer_empresa_id', loginResponse.empresas[0].id.toString());
        }
        
        setUser(userData);

        return { success: true };
      }

      return { success: false, error: 'Resposta inválida do servidor' };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      const errorMessage = err.response?.data?.message || 'Erro ao fazer login';
      return { success: false, error: errorMessage };
    }
  };

  const verifyTwoFactor = async (email: string, code: string) => {
    try {
      const response = await authService.verifyTwoFactor(email, code);
      
      // Check if password change is required after 2FA
      if ('requires_password_change' in response && response.requires_password_change) {
        return { 
          success: false, 
          requiresPasswordChange: true, 
          message: response.message || 'Você precisa alterar sua senha temporária antes de continuar.' 
        };
      }
      
      // Type guard para LoginResponse
      if ('access_token' in response && 'user' in response && response.access_token && response.user) {
        const loginResponse = response as LoginResponse;
        if (!loginResponse.empresas || loginResponse.empresas.length === 0) {
          return {
            success: false,
            error: 'Acesso negado. Nenhum solicitante ativo vinculado a este usuário.',
          };
        }
        const userData: User = {
          id: loginResponse.user.id,
          name: loginResponse.user.name,
          email: loginResponse.user.email,
          role: loginResponse.user.role,
          group_id: loginResponse.user.group_id,
        };

        // Limpar dados de agendamento do localStorage antes de salvar novo login
        // Seguindo o mesmo padrão do scheduling
        console.log('🧹 [AuthContext] Limpando dados de agendamento do localStorage (2FA)');
        const keysToRemove = [
          'customer_employeesData',
          'customer_examsData',
          'customer_stepData',
          'employeesData',
          'examsData',
          'stepData',
          'orderData',
          'pedidoData',
          'pedidoId',
          'pixData',
          'pixError',
          'employeeFormData',
        ];
        
        try {
          keysToRemove.forEach((key) => {
            localStorage.removeItem(key);
          });
          console.log('✅ [AuthContext] Dados de agendamento removidos do localStorage (2FA)');
        } catch (error) {
          console.error('❌ [AuthContext] Erro ao limpar localStorage (2FA):', error);
        }

        localStorage.setItem('customer_token', loginResponse.access_token);
        localStorage.setItem('customer_user', JSON.stringify(userData));
      
        // Save empresas if available
        if (loginResponse.empresas && loginResponse.empresas.length > 0) {
          localStorage.setItem('customer_empresas', JSON.stringify(loginResponse.empresas));
          localStorage.setItem('customer_empresa_id', loginResponse.empresas[0].id.toString());
        }
        
        setUser(userData);

        return { success: true };
      }

      return { success: false, error: 'Resposta inválida do servidor' };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      const errorMessage = err.response?.data?.message || 'Código inválido ou expirado';
      return { success: false, error: errorMessage };
    }
  };

  const changePassword = async (email: string, currentPassword: string, newPassword: string) => {
    try {
      const response = await authService.changePassword({
        email,
        currentPassword,
        newPassword,
      });
      
      // After changing password, if 2FA is enabled for this user
      if ('requires_2fa' in response && response.requires_2fa) {
        return { 
          success: false, 
          requires2FA: true, 
          message: response.message || 'Senha alterada com sucesso! Código de verificação enviado para o seu email.' 
        };
      }

      // If 2FA is not enabled, loginClient returns access_token, user, and empresas directly
      if ('access_token' in response && 'user' in response && response.access_token && response.user) {
        const loginResponse = response as LoginResponse;
        if (!loginResponse.empresas || loginResponse.empresas.length === 0) {
          return {
            success: false,
            error: 'Acesso negado. Nenhum solicitante ativo vinculado a este usuário.',
          };
        }
        const userData: User = {
          id: loginResponse.user.id,
          name: loginResponse.user.name,
          email: loginResponse.user.email,
          role: loginResponse.user.role,
          group_id: loginResponse.user.group_id,
        };

        localStorage.setItem('customer_token', loginResponse.access_token);
        localStorage.setItem('customer_user', JSON.stringify(userData));

        if (loginResponse.empresas && loginResponse.empresas.length > 0) {
          localStorage.setItem('customer_empresas', JSON.stringify(loginResponse.empresas));
          localStorage.setItem('customer_empresa_id', loginResponse.empresas[0].id.toString());
        }

        setUser(userData);
        return { success: true };
      }

      return { success: false, error: 'Resposta inválida do servidor' };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      const errorMessage = err.response?.data?.message || 'Erro ao alterar senha';
      return { success: false, error: errorMessage };
    }
  };

  const updateUser = (updates: Partial<User & { timezone?: string }>) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      localStorage.setItem('customer_user', JSON.stringify(updatedUser));
      if (updates.timezone) {
        localStorage.setItem('customer_timezone', updates.timezone);
      }
    }
  };

  const logout = () => {
    // Limpar dados de autenticação
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_user');
    localStorage.removeItem('customer_empresas');
    localStorage.removeItem('customer_empresa_id');
    
    // Limpar dados de agendamento do localStorage
    console.log('🧹 [AuthContext] Limpando dados de agendamento do localStorage (logout)');
    const keysToRemove = [
      'customer_employeesData',
      'customer_examsData',
      'customer_stepData',
      'employeesData',
      'examsData',
      'stepData',
      'orderData',
      'pedidoData',
      'pedidoId',
      'pixData',
      'pixError',
      'employeeFormData',
    ];
    
    try {
      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
      });
      console.log('✅ [AuthContext] Dados de agendamento removidos do localStorage (logout)');
    } catch (error) {
      console.error('❌ [AuthContext] Erro ao limpar localStorage (logout):', error);
    }
    
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        verifyTwoFactor,
        changePassword,
        updateUser,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}

